// src/components/ui/PopoverWrapper.js
import React from 'react';
import * as Popover from '@radix-ui/react-popover';

/**
 * Standardized Popover Wrapper Component
 * 
 * Provides consistent popover styling across the application
 * Supports both dark and light themes
 * FIXED: Arrow now uses outline technique for clean border connection
 * 
 * @param {React.ReactNode} trigger - The element that triggers the popover
 * @param {React.ReactNode} children - The popover content
 * @param {boolean} open - Control open state
 * @param {Function} onOpenChange - Callback when open state changes
 * @param {'dark' | 'light'} theme - Visual theme of the popover
 * @param {number} sideOffset - Distance from trigger (default: 8)
 * @param {'start' | 'center' | 'end'} align - Alignment relative to trigger
 * @param {'top' | 'right' | 'bottom' | 'left'} side - Which side of trigger to appear on
 * @param {string} className - Additional classes for content
 * @param {number} zIndex - Z-index for the popover (default: 100)
 * @param {string} arrowColorClass - Optional Tailwind classes to override the arrow's color (e.g. 'bg-app-gray-700 fill-app-gray-700')
 */
const PopoverWrapper = ({
  trigger,
  children,
  open,
  onOpenChange,
  theme = 'light',
  sideOffset = 8,
  align = 'center',
  side = 'bottom',
  className = '',
  zIndex = 100,
  arrowColorClass = null,
}) => {
  // Theme-specific classes and colors
  const themeClasses = theme === 'dark'
    ? 'bg-app-gray-800 text-app-white border-0'
    : 'bg-white border border-app-gray-900';

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>
        {trigger}
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side={side}
          sideOffset={sideOffset}
          align={align}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onEscapeKeyDown={() => onOpenChange?.(false)}
          onPointerDownOutside={(e) => {
            e.preventDefault();
            onOpenChange?.(false);
          }}
          onFocusOutside={(e) => {
            e.preventDefault();
          }}
          onInteractOutside={(e) => {
            if (e.type !== 'pointerdown') {
              e.preventDefault();
            }
          }}
          className={`
            ${themeClasses}
            rounded-app-md
            animate-in fade-in-0 zoom-in-95
            data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95
            origin-(--radix-popover-content-transform-origin)
            ${className}
          `}
          style={{ 
            zIndex,
            // Ensure pointer events are enabled
            pointerEvents: 'auto',
          }}
        >
          {children}

          {/* Arrow with clean border using rotated square technique */}
          {theme === 'dark' ? (
            // Dark theme: rotated square matching sidebar/tooltip arrow style
            <Popover.Arrow
              className={`${arrowColorClass ?? 'bg-app-gray-800 fill-app-gray-800'} size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] transition-colors duration-app-fast`}
            />
          ) : (
            // Light theme: rotated square creates perfect border connection
            // For bottom-side popover (arrow points UP toward trigger)
            <Popover.Arrow asChild>
              <div 
                className="relative w-3 h-3 -rotate-45 bg-white border border-app-gray-900 border-t-0 border-r-0"
                style={{
                  // Shift up so the corner (not edge center) touches the popover
                  // For a 12px square rotated -45deg, shift up ~6px
                  top: '-6px',
                }}
              />
            </Popover.Arrow>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default PopoverWrapper;